import { useLayoutEffect } from "react";

/**
 * Adds a subtle fade-up entrance to each <section>'s content as it scrolls into view.
 * Targets the direct, non-positioned children of every <section> inside `rootRef`, so
 * absolutely-positioned background/decoration layers stay put while the content reveals.
 *
 * Uses useLayoutEffect so the initial hidden state is applied before paint (no flash),
 * and respects prefers-reduced-motion.
 *
 * @param {React.RefObject<HTMLElement>} rootRef - ref on the page's root element
 * @param {any} deps - optional value to re-run the setup (e.g. language)
 */
export function useScrollReveal(rootRef, deps) {
  useLayoutEffect(() => {
    const root = rootRef?.current;
    if (!root) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const targets = [];
    root.querySelectorAll("section").forEach((sec) => {
      Array.from(sec.children).forEach((child) => {
        const pos = getComputedStyle(child).position;
        if (pos === "absolute" || pos === "fixed") return;
        if (child.classList.contains("reveal")) return;
        // Skip containers driven by scroll-parallax — they animate their own columns.
        if (child.hasAttribute("data-parallax") || child.querySelector("[data-parallax]")) return;
        child.classList.add("reveal");
        targets.push(child);
      });
    });
    if (!targets.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -6% 0px" }
    );
    targets.forEach((t) => io.observe(t));

    // Safety: ensure nothing stays hidden if the observer never fires (e.g. tab restored).
    const failsafe = setTimeout(() => {
      targets.forEach((t) => t.classList.add("reveal-in"));
    }, 1600);

    return () => {
      io.disconnect();
      clearTimeout(failsafe);
    };
  }, [rootRef, deps]);
}
